import React, { useState, useEffect } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { PURCHASE_TYPES } from '../constants/purchaseTypes';
import { useYearRange } from '../constants/yearRange';

// Format a numeric-ish value as "1,234,567.89" (thousands separators, 2 decimals) for the Purchase Amount field.
// Blank stays blank; an unparseable/mid-typing value is returned unchanged so the field never fights the user.
// This is DISPLAY-ONLY — handleSave strips $/commas/spaces before storing the clean number in purchase_amount.
const formatMoney = (v) => {
  if (v == null || String(v).trim() === '') return '';
  const cleaned = String(v).replace(/[^0-9.]/g, '');
  if (cleaned === '' || cleaned === '.') return '';        // no digits (e.g. "abc") → blank, not 0
  const n = Number(cleaned);
  return isNaN(n) ? String(v) : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// AddPropertyModal — Add AND Edit a property.
//
// ADD (no `property` prop): inserts ONE new row; id derived as `prop-{mpid}`.
// EDIT (`property` prop set): prefills from that row, submits `.update().eq('id', property.id)`.
//   MPID is READ-ONLY in edit — it derives the PK (`prop-{mpid}`) which keys the property's
//   parcels; changing it would orphan them. The id is never changed on update.
//
// Props:
//   isDark         — theme toggle
//   clients        — full clients list (used by the picker when defaultClient is null)
//   defaultClient  — the drilled-in client, or null (form shows a picker if null)
//   property       — OPTIONAL row to edit; when present the modal is in Edit mode
//   onClose        — called when the user cancels or the backdrop is clicked
//   onSuccess      — called with the inserted (add) or updated (edit) property row on save
export default function AddPropertyModal({ isDark, clients, defaultClient, property, allProperties, onClose, onSuccess }) {
  const isEdit = !!property;
  // One source for every value-year picker (folder_year_config, 2021-2028 fallback) — same hook the parcel
  // modal, Reports and the worklists use, so First Year cannot offer a different range from the year tabs.
  const { years } = useYearRange();
  const [form, setForm] = useState(() => property ? {
    client_id: property.client_id || '',
    name: property.name || '',
    address: property.address || '',
    city: property.city || '',
    state: property.state || '',
    postal_code: property.postal_code || '',
    county: property.county || '',
    consultant_firm: property.consultant_firm || '',
    location_code: property.location_code || '',
    master_property_id: property.master_property_id != null ? String(property.master_property_id) : '',
    purchase_date: property.purchase_date || '',
    purchase_amount: property.purchase_amount != null ? formatMoney(property.purchase_amount) : '',
    purchase_type: property.purchase_type || '',
    first_year: property.first_year || '',
  } : {
    client_id: defaultClient?.id || '',
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    county: '',
    consultant_firm: '',
    location_code: '',
    master_property_id: '',
    purchase_date: '',
    purchase_amount: '',
    purchase_type: '',
    first_year: '',
  });
  const [consultantFirms, setConsultantFirms] = useState([]);   // consultants.company — suggestions (free-text allowed)
  useEffect(() => { supabase.from('consultants').select('company').order('company').then(({ data }) => setConsultantFirms((data || []).map(c => c.company).filter(Boolean)), () => {}); }, []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pickClient, setPickClient] = useState(!defaultClient && !property);

  const c = isDark ? {
    card: '#2F3854', border: '#374060', text: '#e8eaf2',
    muted: 'var(--slate)', subtext: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)',
    muted: 'var(--slate)', subtext: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8',
  };

  const s = {
    label:    { display:'block', fontSize:'11px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input:    { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'Public Sans',system-ui,sans-serif" },
    select:   { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btn:      { padding:'9px 18px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color: 'white', fontSize:'13px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding:'9px 16px', background:'transparent', border:`1px solid ${c.border}`, borderRadius:'7px', color:c.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
  };

  function set(field, val) { setForm(prev => ({ ...prev, [field]: val })); }

  function validate() {
    if (!form.client_id) return 'Client is required.';
    if (!form.name.trim()) return 'Property Name is required.';
    const mpidRaw = form.master_property_id.trim();
    if (!mpidRaw) return 'Master Property ID is required.';
    if (!/^\d+$/.test(mpidRaw)) return 'Master Property ID must be a whole number.';
    if (!form.address.trim()) return 'Property Address is required.';
    const mpid = parseInt(mpidRaw, 10);
    const derivedId = `prop-${mpid}`;
    // Client-side dedupe against the loaded property list (the parent's
    // allProperties is the same list the success handler refreshes, so newly
    // added rows are included). The DB PK on id is the backstop for the rare
    // two-tabs race; that error would still surface inline via the catch.
    // Skip the row being edited (its own MPID isn't a conflict). In add mode `property` is undefined,
    // so `p.id !== undefined` is always true and the check is unchanged.
    if (Array.isArray(allProperties) && allProperties.some(p => (p.id === derivedId || p.master_property_id === mpid) && p.id !== property?.id)) {
      return `Master Property ID ${mpid} already exists — each property needs a unique ID.`;
    }
    return null;
  }

  async function handleSave() {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError('');

    const parsedMpid = form.master_property_id.trim()
      ? parseInt(form.master_property_id.trim(), 10)
      : null;
    const cleanedAmount = String(form.purchase_amount).replace(/[$,\s]/g, '');
    const parsedAmount = cleanedAmount ? parseFloat(cleanedAmount) : null;

    // Phase-1 id scheme (per Sigertax transition plan): id is derived from
    // the user-supplied MPID as 'prop-{mpid}', matching every existing row in
    // the DB (verified across all 2,052 rows). Phase-2 will auto-assign MPID.
    const payload = {
      id:                 `prop-${parsedMpid}`,
      client_id:          form.client_id,
      name:               form.name.trim(),
      address:            form.address.trim(),
      city:               form.city.trim() || null,
      state:              form.state.trim() || null,
      postal_code:        form.postal_code.trim() || null,
      county:             form.county.trim() || null,
      consultant_firm:    form.consultant_firm.trim() || null,   // drives consultant DOCUMENT ACCESS (DocumentsBrowser); TEXT, not a FK
      location_code:      form.location_code.trim() || null,
      master_property_id: parsedMpid,
      purchase_date:      form.purchase_date || null,
      purchase_amount:    (parsedAmount != null && !isNaN(parsedAmount)) ? parsedAmount : null,
      purchase_type:      form.purchase_type || null,
      // Blank must persist as NULL, not '' — null is the "always ours" reading is_represented will rely on.
      first_year:         form.first_year || null,
    };

    try {
      let data, err;
      if (isEdit) {
        // Update in place; never change the PK-deriving id (MPID is read-only in edit, so it's unchanged anyway).
        const updateFields = { ...payload }; delete updateFields.id;
        ({ data, error: err } = await supabase.from('properties').update(updateFields).eq('id', property.id).select().single());
      } else {
        ({ data, error: err } = await supabase.from('properties').insert(payload).select().single());
      }
      if (err) throw err;
      onSuccess(data);
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  const selectedClient = clients.find(cl => cl.id === form.client_id);

  return (
    <div {...backdropProps(onClose)}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:c.card, borderRadius:'14px', padding:'28px', maxWidth:'640px', width:'100%', boxShadow:'0 8px 40px rgba(0,0,0,0.3)', maxHeight:'calc(100vh - 40px)', overflowY:'auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
          <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'20px', fontWeight:'700', color:c.text }}>{isEdit ? 'Edit Property' : 'Add Property'}</div>
          <button onClick={onClose} type="button"
            style={{ background:'none', border:'none', color:c.muted, fontSize:'18px', cursor:'pointer', padding:'0 4px', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ fontSize:'12px', color:c.muted, marginBottom:'22px' }}>{isEdit ? 'Update this property’s details.' : 'Create a new property under a client. Parcels and contacts can be added next.'}</div>

        {/* Client */}
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Client *</label>
          {!pickClient && selectedClient ? (
            <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px' }}>
              <span style={{ fontSize:'14px', fontWeight:'600', color:'var(--red)' }}>🏦 {selectedClient.name}</span>
              <button type="button" onClick={() => { setPickClient(true); set('client_id', ''); }}
                style={{ marginLeft:'auto', background:'none', border:'none', color:c.muted, fontSize:'11px', cursor:'pointer', textDecoration:'underline' }}>change</button>
            </div>
          ) : (
            <select style={s.select} value={form.client_id} onChange={e => set('client_id', e.target.value)}>
              <option value="">— Pick a client —</option>
              {clients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
            </select>
          )}
        </div>

        {/* Property Name */}
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Property Name *</label>
          <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. North Bethesda Market" />
        </div>

        {/* Address */}
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Property Address *</label>
          <input style={s.input} value={form.address} onChange={e => set('address', e.target.value)} placeholder="100 Main St" />
        </div>

        {/* City / State / Zip / County */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1.5fr', gap:'10px', marginBottom:'14px' }}>
          <div>
            <label style={s.label}>City</label>
            <input style={s.input} value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>State</label>
            <input style={s.input} value={form.state} onChange={e => set('state', e.target.value.toUpperCase())} maxLength={2} placeholder="e.g. TX" />
          </div>
          <div>
            <label style={s.label}>Zip</label>
            <input style={s.input} value={form.postal_code} onChange={e => set('postal_code', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>County</label>
            <input style={s.input} value={form.county} onChange={e => set('county', e.target.value)} placeholder="e.g. Harris" />
          </div>
        </div>

        {/* Consultant — property-level; drives consultant document access. Free-text + picker. */}
        <div style={{ marginBottom:'14px' }}>
          <label style={s.label}>Consultant</label>
          <input list="apm-consultant-firms" style={s.input} value={form.consultant_firm} onChange={e => set('consultant_firm', e.target.value)} placeholder="Pick or type a consultant firm" />
          <datalist id="apm-consultant-firms">{consultantFirms.map(f => <option key={f} value={f} />)}</datalist>
        </div>

        {/* Location ID / MPID */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px' }}>
          <div>
            <label style={s.label}>Location ID</label>
            <input style={s.input} value={form.location_code} onChange={e => set('location_code', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>MPID *</label>
            <input style={{ ...s.input, ...(isEdit ? { background: c.card, color: c.muted, cursor: 'not-allowed' } : {}) }} value={form.master_property_id} onChange={e => set('master_property_id', e.target.value)} placeholder="numeric" disabled={isEdit} />
            <div style={{ fontSize:'10px', color:c.muted, marginTop:'4px' }}>{isEdit ? 'Locked — the MPID keys this property’s parcels.' : 'Sigertax-assigned master property ID. Required.'}</div>
          </div>
        </div>

        {/* Purchase fields */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'14px' }}>
          <div>
            <label style={s.label}>Purchase Date</label>
            <input type="date" style={s.input} value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Purchase Amount</label>
            <input style={s.input} value={form.purchase_amount} onChange={e => set('purchase_amount', e.target.value)} onBlur={e => set('purchase_amount', formatMoney(e.target.value))} placeholder="$0.00" />
          </div>
          <div>
            {/* ⛔ FIRST YEAR — the year this PROPERTY came on board, the mirror of properties.retired_year.
                Distinct from Purchase Date: a building can be bought long before (or after) we represent it.
                Blank = always ours, which is every existing property until someone sets one.
                Parcels carry their OWN first_year: 18 East has been ours since 2021 while its parcels come and
                go with tenants, so a parcel created in 2024 must not be counted back to the building's start.
                ⛔ NOTHING GATES ON THIS YET — the is_represented start clause and its 7 callers ship next. */}
            <label style={s.label}>First Year (representation)</label>
            <select style={s.select} value={form.first_year} onChange={e => set('first_year', e.target.value)}
              title="The year this property came into our representation. Blank = we have always had it.">
              <option value="">— Always ours —</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Purchase Type</label>
            <select style={s.select} value={form.purchase_type} onChange={e => set('purchase_type', e.target.value)}>
              <option value="">—</option>
              {PURCHASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div style={{ padding:'10px 12px', marginBottom:'14px', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'7px', fontSize:'12px', color:'#f87171' }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'22px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ ...s.btn, opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : '+ Add Property')}
          </button>
        </div>
      </div>
    </div>
  );
}
